import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ChevronRight, Lock, Plus } from 'lucide-react'
import { api } from '../../lib/api.js'
import { toast } from '../../components/feedback/Toast.jsx'
import SwipeRow from '../../components/SwipeRow.jsx'
import ClientForm from './ClientForm.jsx'
import { audioLine } from './fields.js'
import { fmtDayMedium } from '../../lib/date.js'

/**
 * The 5-second mid-session lookup. All 50 clients ship in one response;
 * filtering is instant and local. The chips surface the fields an engineer
 * actually opens this list for.
 */
export default function ClientsPage() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState('')
  const [adding, setAdding] = useState(false)
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data, isLoading } = useQuery({ queryKey: ['clients'], queryFn: () => api('/clients') })
  const canEdit = ['admin', 'editor'].includes(me.data?.user?.role)

  // A client record is its page — deleting one is the page's soft delete.
  async function deleteClient(c) {
    if (!window.confirm(`Delete “${c.name}”?`)) return
    try {
      await api(`/pages/${c.pageId}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  const rows = (data?.clients || []).filter((c) => {
    const f = filter.trim().toLowerCase()
    if (!f) return true
    return [c.name, c.parentName, c.remotePref, audioLine(c), c.remoteMethods.join(' '), c.fileDelivery, c.details].some(
      (v) => v && v.toLowerCase().includes(f)
    )
  })

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3 border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">Client Info</h1>
        {canEdit && (
          <button onClick={() => setAdding(true)} className="tap flex shrink-0 items-center gap-1.5 rounded-xl bg-navy px-3.5 py-2 text-sm font-medium text-white">
            <Plus size={15} /> New client
          </button>
        )}
      </div>

      {/* Client Set-ups is a page, not a client record, so the record list
          below never shows it — without this row it's reachable only via
          search (Charles couldn't find it, 2026-08-04). */}
      <Link
        to="/clients/client-set-ups"
        className="card tap mb-2 flex items-center gap-3 px-4 py-3 active:bg-grouped"
      >
        <BookOpen size={16} className="shrink-0 text-ink" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">Client Set-ups</span>
        <ChevronRight size={16} className="shrink-0 text-faint" />
      </Link>

      {/* Embargoed Projects Passwords: its own Logins page (Charles,
          2026-09-29). Until then this row opened a Google Sheet. */}
      <Link
        to="/clients/embargo-logins"
        className="card tap mb-4 flex items-center gap-3 px-4 py-3 active:bg-grouped"
      >
        <Lock size={16} className="shrink-0 text-ink" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">Embargoed Projects Passwords</span>
        <ChevronRight size={16} className="shrink-0 text-faint" />
      </Link>

      <input
        type="search"
        placeholder="Filter clients…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="tap mb-4 w-full rounded-xl border border-hairline bg-grouped px-4 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
      />

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <div className="card divide-y divide-hairline overflow-hidden">
          {rows.map((c) => (
            <MaybeSwipe key={c.id} enabled={canEdit} onDelete={() => deleteClient(c)}>
            <Link to={`/clients/${c.slug}`} className="flex items-center gap-3 px-4 py-3 active:bg-grouped">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="truncate text-sm font-medium text-ink">{c.name}</span>
                  {c.parentName && <span className="shrink-0 text-xs text-faint">· {c.parentName}</span>}
                  {c.latestNoteAt && <span className="shrink-0 text-xs text-muted">· updated {fmtDayMedium(c.latestNoteAt)}</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {audioLine(c) && <Chip>{audioLine(c).replace(' · ', ' ')}</Chip>}
                  {c.remoteMethods.map((m) => (
                    <Chip key={m} tone="film">{m}</Chip>
                  ))}
                </div>
              </div>
              <ChevronRight size={16} className="shrink-0 text-faint" />
            </Link>
            </MaybeSwipe>
          ))}
          {rows.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted">No matches.</p>}
        </div>
      )}

      {adding && <ClientForm onClose={() => setAdding(false)} />}
    </div>
  )
}

/** Swipe-left-to-delete for editors; viewers get the plain row. */
function MaybeSwipe({ enabled, onDelete, children }) {
  if (!enabled) return children
  return <SwipeRow onDelete={onDelete}>{children}</SwipeRow>
}

function Chip({ children, tone }) {
  return (
    <span
      className={
        // outlined, print-style chip — border does the color work
        'rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
        (tone === 'film' ? 'border-film-fg text-film-fg' : 'border-music-fg text-music-fg')
      }
    >
      {children}
    </span>
  )
}
